import api from "api";
import { supportApiBase } from "api/constants";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { queryKeys } from "utils/misc";
import { Country } from "utils/market";
import { useId } from "utils/hooks";
import { Hcp } from "routes/hcp/types";

export { supportApiBase };

export const useHCPs = () => {
  const key = queryKeys.hcps();
  return useQuery(key, () => api.parse("GET /health_care_providers"));
};

export const useHcp = (overrideId?: number) => {
  const defaultId = useId();
  const id = overrideId ?? defaultId;
  const key = queryKeys.hcp(id);
  return useQuery(key, () =>
    api.call(`/health_care_providers/${id}`, { root: supportApiBase }).then(res => res.json() as Promise<Hcp>)
  );
};

export enum HcpSearchCategory {
  name = "name",
  id = "id",
}

export interface HcpSearchParams {
  country: Country;
  category: HcpSearchCategory;
  search: string;
}

export const useSearchHcps = (params: HcpSearchParams) => {
  const { category, search } = params;
  const key = queryKeys.hcpSearch(params);

  return useQuery(
    key,
    async () => {
      const queryParams = new URLSearchParams();
      if (category === HcpSearchCategory.id) {
        queryParams.set("id", search);
      } else {
        queryParams.set("search", search);
      }
      const response = await api.call(`/health_care_providers?${queryParams}`, { root: supportApiBase });
      return response.json() as Promise<import("routes/hcp/types").HcpListResponse>;
    },
    { enabled: !!search }
  );
};

export interface PayerOption {
  id: number;
  name: string;
}

interface PayerSearchResponse {
  items: PayerOption[];
}

export type PayerKind = "provider_group" | "insurance";

// With a kind set, an empty search lists every payer of that kind
export const useSearchPayers = (search: string, kind?: PayerKind) => {
  const key = queryKeys.payerSearch(search, kind);

  return useQuery(
    key,
    async () => {
      const queryParams = new URLSearchParams();
      if (search) queryParams.set("search", search);
      if (kind) queryParams.set("kind", kind);
      const response = await api.call(`/payers?${queryParams}`, { root: supportApiBase });
      return response.json() as Promise<PayerSearchResponse>;
    },
    { enabled: !!kind || search.length >= 2 }
  );
};

export interface FeatureFlag {
  flag: string;
  description?: string;
  enabled: boolean;
}

interface FeatureFlagsResponse {
  feature_flags: FeatureFlag[];
}

export const useHcpFeatureFlags = (overrideId?: number) => {
  const defaultId = useId();
  const id = overrideId ?? defaultId;
  const key = queryKeys.hcpFeatureFlags(id);

  return useQuery(key, async () => {
    const response = await api.call(`/health_care_providers/${id}/feature_flags`, { root: supportApiBase });
    return response.json() as Promise<FeatureFlagsResponse>;
  });
};

interface UpdateFeatureFlagParams {
  hcpId: number;
  flag: string;
  enabled: boolean;
}

export interface CreateHcpInput {
  name: string;
  email?: string;
  phone_number?: string;
  address: string;
  slug: string;
  iana_timezone: string;
  category?: number;
  admits_patients?: boolean;
  invitation_payer_id?: number;
  health_care_organization_id?: number;
}

export const useCreateHcp = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: CreateHcpInput) => {
      const response = await api.call("/health_care_providers", {
        method: "POST",
        data,
        root: supportApiBase,
      });
      return response.json() as Promise<Hcp>;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKeys.hcps());
        queryClient.invalidateQueries(queryKeys.hcpSearch());
      },
    }
  );
};

export const useUpdateHcpFeatureFlag = (onError?: (err: unknown) => void) => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ hcpId, flag, enabled }: UpdateFeatureFlagParams) => {
      await api.call(`/health_care_providers/${hcpId}/feature_flags`, {
        method: "PUT",
        data: { flag, enabled },
        root: supportApiBase,
      });
    },
    {
      onMutate: async ({ hcpId, flag, enabled }: UpdateFeatureFlagParams) => {
        const key = queryKeys.hcpFeatureFlags(hcpId);
        await queryClient.cancelQueries(key);
        const previous = queryClient.getQueryData<FeatureFlagsResponse>(key);

        queryClient.setQueryData<FeatureFlagsResponse | undefined>(key, old => {
          if (!old) return old;
          return {
            ...old,
            feature_flags: old.feature_flags.map(f => (f.flag === flag ? { ...f, enabled } : f)),
          };
        });

        return { previous, key } as const;
      },
      onError: (
        err: unknown,
        _: UpdateFeatureFlagParams,
        context:
          | { previous: FeatureFlagsResponse | undefined; key: ReturnType<typeof queryKeys.hcpFeatureFlags> }
          | undefined
      ) => {
        if (context?.previous) {
          queryClient.setQueryData(context.key, context.previous);
        }
        onError?.(err);
      },
      onSettled: (_, __, { hcpId }) => {
        queryClient.invalidateQueries(queryKeys.hcpFeatureFlags(hcpId));
      },
    }
  );
};
